import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { purchaseRequestsService, offersService } from '../../services/supabaseService';
import { aiMatchingService } from '../../services/aiMatchingService';
import { realTimeMonitoringService } from '../../services/realTimeMonitoringService';
import Header from '../../components/ui/Header';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import DealStatusIndicator from '../../components/ui/DealStatusIndicator';
import CreateRequestForm from './components/CreateRequestForm';
import RequestsTable from './components/RequestsTable';
import OffersPanel from './components/OffersPanel';
import FilterBar from './components/FilterBar';

const BuyerDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [requests, setRequests] = useState([]);
  const [offers, setOffers] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    destination: ''
  });
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiMatchingMode, setAiMatchingMode] = useState('BALANCED');
  const [isAiMatching, setIsAiMatching] = useState(false);
  const [aiMatchResult, setAiMatchResult] = useState(null);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/authentication-screen');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user?.id) {
      loadPurchaseRequests();
      // Start real-time monitoring for automatic rematch
      realTimeMonitoringService?.startMonitoring();
    }
    
    return () => {
      // Cleanup monitoring on unmount
      realTimeMonitoringService?.stopMonitoring();
    };
  }, [user]);

  const loadPurchaseRequests = async () => {
    try {
      setLoading(true);
      const data = await purchaseRequestsService?.getByBuyerId(user?.id);
      setRequests(data || []);
      
      if (data?.length > 0) {
        const requestIds = data?.map(r => r?.id);
        const offersData = await offersService?.getByRequestIds(requestIds);
        setOffers(offersData || []);
      }
    } catch (error) {
      console.error('Load purchase requests error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async (formData) => {
    try {
      const categoryMap = {
        'electronics': 'electronics',
        'fashion': 'fashion',
        'cosmetics': 'cosmetics',
        'food': 'food',
        'books': 'books',
        'sports': 'sports',
        'toys': 'toys',
        'health': 'health'
      };

      const newRequest = {
        buyerId: user?.id,
        itemName: formData?.itemName,
        category: categoryMap?.[formData?.category] || 'electronics',
        originCountry: formData?.originCountry,
        destinationCountry: formData?.destinationCountry,
        budget: parseInt(formData?.budget?.replace(/,/g, '')),
        deliveryDate: formData?.deliveryDate,
        carrierFee: parseInt(formData?.carrierFee?.replace(/,/g, '')),
        notes: formData?.notes,
        status: 'active'
      };

      const created = await purchaseRequestsService?.create(newRequest);
      if (created) {
        setRequests((prev) => [created, ...prev]);
      }
    } catch (error) {
      console.error('Create request error:', error);
    }
  };

  const handleViewOffers = (requestId) => {
    setSelectedRequestId(requestId);
    const requestOffers = offers?.filter((offer) => offer?.requestId === requestId);
    if (requestOffers?.length > 0) {
      window.scrollTo({ top: document.getElementById('offers-section')?.offsetTop - 100, behavior: 'smooth' });
    }
  };

  const handleAcceptOffer = async (offerId) => {
    try {
      const offer = offers?.find((o) => o?.id === offerId);
      if (offer) {
        const deal = await offersService?.acceptOffer(
          offerId,
          user?.id,
          offer?.travelerId,
          offer?.requestId
        );
        
        if (deal) {
          await loadPurchaseRequests();
          navigate('/deal-room-page', { state: { dealId: deal?.id } });
        }
      }
    } catch (error) {
      console.error('Accept offer error:', error);
    }
  };

  const handleDeclineOffer = async (offerId) => {
    try {
      const success = await offersService?.declineOffer(offerId);
      if (success) {
        setOffers((prev) => prev?.filter((offer) => offer?.id !== offerId));
        await loadPurchaseRequests();
      }
    } catch (error) {
      console.error('Decline offer error:', error);
    }
  };

  const handleAIMatch = async (requestId) => {
    try {
      setIsAiMatching(true);
      setAiMatchResult(null);
      
      const result = await aiMatchingService?.matchRequestWithTraveler(
        requestId,
        aiMatchingMode
      );
      
      setAiMatchResult(result);
      
      if (result?.matched) {
        // Reload requests to show updated status
        await loadPurchaseRequests();
        
        // Track AI matching event
        if (typeof window !== 'undefined' && window.trackEvent && typeof window.trackEvent === 'function') {
          window.trackEvent('ai_matching_success', {
            request_id: requestId,
            matching_mode: aiMatchingMode,
            match_score: result?.matchScore,
            execution_time: result?.executionTime,
            page: '/buyer-dashboard'
          });
        }
      } else {
        // Track no match event
        if (typeof window !== 'undefined' && window.trackEvent && typeof window.trackEvent === 'function') {
          window.trackEvent('ai_matching_no_match', {
            request_id: requestId,
            matching_mode: aiMatchingMode,
            alternatives_count: result?.alternatives?.length || 0,
            page: '/buyer-dashboard'
          });
        }
      }
    } catch (error) {
      console.error('AI matching error:', error);
      setAiMatchResult({
        matched: false,
        error: error?.message || 'AI matching failed'
      });
    } finally {
      setIsAiMatching(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const filteredRequests = requests?.filter((request) => {
    if (filters?.status && request?.status !== filters?.status) return false;
    if (filters?.category && request?.category !== filters?.category) return false;
    if (filters?.destination && request?.destinationCountry !== filters?.destination) return false;
    return true;
  });

  const displayedOffers = selectedRequestId ?
  offers?.filter((offer) => offer?.requestId === selectedRequestId) :
  offers;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <DealStatusIndicator />
      
      <main className="pt-20 pb-20 lg:pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-6 md:space-y-8">
          <CreateRequestForm
            onSubmit={handleCreateRequest}
            currentLanguage={currentLanguage} />
          

          <FilterBar
            filters={filters}
            onFilterChange={handleFilterChange}
            currentLanguage={currentLanguage} />
          

          <RequestsTable
            requests={filteredRequests}
            offers={offers}
            onViewOffers={handleViewOffers}
            onAIMatch={handleAIMatch}
            aiMatchingMode={aiMatchingMode}
            onAIMatchingModeChange={setAiMatchingMode}
            isAiMatching={isAiMatching}
            aiMatchResult={aiMatchResult}
            currentLanguage={currentLanguage}
          />
          

          <div id="offers-section">
            <OffersPanel
              offers={displayedOffers}
              onAccept={handleAcceptOffer}
              onDecline={handleDeclineOffer}
              currentLanguage={currentLanguage} />
            
          </div>
        </div>
      </main>

      <BottomTabNavigation />
    </div>);

};

export default BuyerDashboard;